// Send later on the web (M64, D239, docs/chat-round-six.md). The message is
// sealed here as a text message would be and kept by the server until its
// time; after a key rotation any of my devices seals it again.
import { SCHEDULE_LIMITS, type ScheduledMessage } from '@twinden/shared';
import { api, ApiError } from './api';
import { t } from '../i18n';
import { appLocale } from './format';
import { isE2EEReady, sendSealed, type OpenedE2EE } from './e2ee/session';

/** A scheduled message as this device holds it, opened if it could be. */
export type ScheduledView = ScheduledMessage & { e2ee?: OpenedE2EE };

export interface SendLaterChoice {
  key: 'hour' | 'tonight' | 'tomorrow';
  at: Date;
}

/**
 * The quick picks (D239): in an hour, tonight at 8 pm (while that's still
 * far enough off), tomorrow at 9 am.
 */
export function sendLaterChoices(now: Date = new Date()): SendLaterChoice[] {
  const choices: SendLaterChoice[] = [
    { key: 'hour', at: new Date(now.getTime() + 60 * 60 * 1000) },
  ];
  const tonight = new Date(now);
  tonight.setHours(20, 0, 0, 0);
  if (tonight.getTime() - now.getTime() >= 2 * 60 * 60 * 1000) {
    choices.push({ key: 'tonight', at: tonight });
  }
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(9, 0, 0, 0);
  choices.push({ key: 'tomorrow', at: tomorrow });
  return choices;
}

/** What's wrong with a picked time, in words, or null. */
export function scheduleTimeError(
  at: Date | null,
  now: number = Date.now()
): string | null {
  if (!at || Number.isNaN(at.getTime())) return t('chat.sendLater.invalid');
  // The sheet holds people to the limits exactly; the server allows a
  // minute's grace for the trip.
  if (at.getTime() < now + SCHEDULE_LIMITS.MIN_LEAD_MS) {
    return t('chat.sendLater.tooSoon');
  }
  if (at.getTime() > now + SCHEDULE_LIMITS.MAX_LEAD_MS) {
    return t('chat.sendLater.tooFar');
  }
  return null;
}

/** A refusal from the server in the reader's language, when we have one. */
export function scheduleErrorText(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  switch (err.code) {
    case 'SCHEDULE_TOO_SOON':
      return t('chat.sendLater.tooSoon');
    case 'SCHEDULE_TOO_FAR':
      return t('chat.sendLater.tooFar');
    case 'VALIDATION_ERROR':
      return t('chat.sendLater.invalid');
    default:
      return null;
  }
}

/** The time field's first value: an hour from now, on a 5-minute mark. */
export function defaultScheduleTime(now: number = Date.now()): Date {
  const step = 5 * 60 * 1000;
  return new Date(Math.ceil((now + 60 * 60 * 1000) / step) * step);
}

/** "Tue 9:00" (this week) or "3 Nov, 9:00". */
export function scheduledLabel(iso: string, now: number = Date.now()): string {
  const at = new Date(iso);
  const soon = at.getTime() - now < 6 * 86400_000;
  return at.toLocaleString(appLocale(), {
    ...(soon
      ? { weekday: 'short' as const }
      : { day: 'numeric' as const, month: 'short' as const }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** `<input type="datetime-local">`'s value for a time, in local time. */
export function localInputValue(at: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

export class KeysNotReadyError extends Error {
  constructor() {
    super('KEYS_NOT_READY');
  }
}

/**
 * Seals a text message on this device and hands it to the server for
 * later (D239). Seals again once if the chat moved to a newer key.
 */
export async function scheduleText(input: {
  conversationId: string;
  text: string;
  scheduledFor: Date;
  mentions?: string[];
  mentionsEveryone?: boolean;
  replyToId?: string;
}) {
  if (!isE2EEReady()) throw new KeysNotReadyError();
  const clientId = crypto.randomUUID();
  return sendSealed(
    input.conversationId,
    clientId,
    { v: 1, text: input.text },
    { type: 'text' },
    (sealed) =>
      api.scheduleMessage(input.conversationId, {
        clientId,
        content: sealed.content,
        keyEpoch: sealed.keyEpoch,
        encryption: sealed.encryption,
        mentions: input.mentions,
        mentionsEveryone: input.mentionsEveryone,
        replyToId: input.replyToId,
        scheduledFor: input.scheduledFor.toISOString(),
      })
  );
}

/** The text a scheduled message holds, if this device could open it. */
export function scheduledText(item: ScheduledView): string | null {
  // Plain only without any encryption: a newer app's (#82) stays unopened,
  // and plain text dated after the chat's encryption isn't shown (D347).
  if (item.e2ee?.unverified) return null;
  if (!item.encryption) return item.content;
  return item.e2ee?.envelope?.text ?? null;
}

/**
 * Seals a scheduled message again under the chat's current key, with its
 * own clientId (D239), after a rotation. False when this device can't
 * open it (no key for that epoch here) or has no keys yet.
 */
export async function resealScheduled(item: ScheduledView): Promise<boolean> {
  const text = scheduledText(item);
  if (text === null || !isE2EEReady()) return false;
  try {
    await sendSealed(
      item.conversationId,
      item.clientId,
      { v: 1, text },
      { type: 'text' },
      (sealed) =>
        api.resealScheduled(item.conversationId, item.id, {
          content: sealed.content,
          keyEpoch: sealed.keyEpoch,
          encryption: sealed.encryption,
        })
    );
    return true;
  } catch (err) {
    // Gone or being sent by another of my devices: nothing to do.
    if (
      err instanceof ApiError &&
      (err.code === 'SCHEDULED_NOT_FOUND' ||
        err.code === 'SCHEDULED_SENDING' ||
        // Another of my devices sealed it again first.
        err.code === 'RESEAL_NOT_NEEDED')
    ) {
      return true;
    }
    throw err;
  }
}

const resealing = new Map<string, Promise<boolean>>();

/**
 * Seals a row again once at a time on this device: the app-wide reseal and
 * Send now share the same attempt instead of racing two PATCHes (D239).
 */
export function resealOnce(item: ScheduledView): Promise<boolean> {
  const running = resealing.get(item.id);
  if (running) return running;
  const attempt = resealScheduled(item).finally(() =>
    resealing.delete(item.id)
  );
  resealing.set(item.id, attempt);
  return attempt;
}

/**
 * Looks for my scheduled messages that need sealing again and seals the
 * ones this device can (D239).
 */
export async function resealWaiting(items?: ScheduledView[]): Promise<number> {
  const list = items ?? (await api.getAllScheduled()).data;
  let done = 0;
  for (const item of list as ScheduledView[]) {
    if (!item.needsReseal) continue;
    try {
      if (await resealOnce(item)) done += 1;
    } catch {
      // Tried again on the next connect or event.
    }
  }
  return done;
}
